/**
 * Game Logic Utilities
 * Contains all game logic functions for tic-tac-toe
 */

import { Board, Player, WinnerResult, DifficultyLevel } from "./types";

// All possible winning combinations in a 3x3 grid
// Format: [cell1, cell2, cell3] where cells are indices 0-8
export const WINNING_COMBINATIONS = [
  [0, 1, 2], // Top row
  [3, 4, 5], // Middle row
  [6, 7, 8], // Bottom row
  [0, 3, 6], // Left column
  [1, 4, 7], // Middle column
  [2, 5, 8], // Right column
  [0, 4, 8], // Main diagonal (top-left to bottom-right)
  [2, 4, 6], // Anti-diagonal (top-right to bottom-left)
];

/**
 * Checks if there's a winner on the current board
 * @param currentBoard - The current game board state
 * @returns Object containing the winner (X, O, Draw, or null) and the winning line indices
 */
export const checkWinner = (currentBoard: Board): WinnerResult => {
  // Check all winning combinations
  for (const [a, b, c] of WINNING_COMBINATIONS) {
    if (
      currentBoard[a] &&
      currentBoard[a] === currentBoard[b] &&
      currentBoard[a] === currentBoard[c]
    ) {
      return { winner: currentBoard[a], line: [a, b, c] };
    }
  }
  // Check for draw (all cells filled, no winner)
  if (currentBoard.every((cell) => cell !== null)) {
    return { winner: "Draw", line: [] };
  }
  return { winner: null, line: [] };
};

/**
 * Minimax recursive algorithm for unbeatable (Hard) AI
 * Evaluates board positions:
 * - Computer (O) win: +10 - depth (prefers faster wins)
 * - Player (X) win: depth - 10 (prefers prolonged survival if losing)
 * - Draw: 0
 */
const minimax = (
  board: Board,
  depth: number,
  isMaximizing: boolean
): number => {
  const result = checkWinner(board);
  if (result.winner === "O") return 10 - depth;
  if (result.winner === "X") return depth - 10;
  if (result.winner === "Draw") return 0;

  if (isMaximizing) {
    let maxScore = -Infinity;
    for (let i = 0; i < 9; i++) {
      if (board[i] === null) {
        board[i] = "O";
        const score = minimax(board, depth + 1, false);
        board[i] = null;
        if (score > maxScore) {
          maxScore = score;
        }
      }
    }
    return maxScore;
  } else {
    let minScore = Infinity;
    for (let i = 0; i < 9; i++) {
      if (board[i] === null) {
        board[i] = "X";
        const score = minimax(board, depth + 1, true);
        board[i] = null;
        if (score < minScore) {
          minScore = score;
        }
      }
    }
    return minScore;
  }
};

/**
 * Hard AI: Perfect, unbeatable play using Minimax
 */
const getHardMove = (board: Board, emptyCells: number[]): number => {
  // If first move of game on empty board, pick center or corner for natural variety
  if (emptyCells.length === 9) {
    const openings = [0, 2, 4, 6, 8];
    return openings[Math.floor(Math.random() * openings.length)];
  }

  let bestScore = -Infinity;
  let bestMoves: number[] = [];

  for (const idx of emptyCells) {
    board[idx] = "O";
    const score = minimax(board, 0, false);
    board[idx] = null;

    if (score > bestScore) {
      bestScore = score;
      bestMoves = [idx];
    } else if (score === bestScore) {
      bestMoves.push(idx);
    }
  }

  return bestMoves.length > 0
    ? bestMoves[Math.floor(Math.random() * bestMoves.length)]
    : emptyCells[0];
};

/**
 * Medium AI: Strategic heuristic
 * - Always takes immediate win
 * - Blocks player ~75% of the time (can occasionally miss a trap)
 * - Prefers center and corners, but beatable with forks
 */
const getMediumMove = (board: Board, emptyCells: number[]): number => {
  // 1. Try to win if possible (100%)
  for (const idx of emptyCells) {
    const testBoard = [...board];
    testBoard[idx] = "O";
    if (checkWinner(testBoard).winner === "O") return idx;
  }

  // 2. Block player from winning (~75% of the time)
  const playerWinningMoves: number[] = [];
  for (const idx of emptyCells) {
    const testBoard = [...board];
    testBoard[idx] = "X";
    if (checkWinner(testBoard).winner === "X") {
      playerWinningMoves.push(idx);
    }
  }

  if (playerWinningMoves.length > 0 && Math.random() < 0.75) {
    return playerWinningMoves[
      Math.floor(Math.random() * playerWinningMoves.length)
    ];
  }

  // 3. Take center if available (~65% chance)
  if (emptyCells.includes(4) && Math.random() < 0.65) {
    return 4;
  }

  // 4. Take a corner if available (~60% chance)
  const corners = [0, 2, 6, 8].filter((idx) => emptyCells.includes(idx));
  if (corners.length > 0 && Math.random() < 0.6) {
    return corners[Math.floor(Math.random() * corners.length)];
  }

  // 5. Take any available cell
  return emptyCells[Math.floor(Math.random() * emptyCells.length)];
};

/**
 * Easy AI: Casual play
 * - Mostly makes random moves (85%)
 * - Has a small 15% chance to complete an obvious 3-in-a-row
 * - Never deliberately blocks player wins, making it very accessible
 */
const getEasyMove = (board: Board, emptyCells: number[]): number => {
  if (Math.random() < 0.15) {
    for (const idx of emptyCells) {
      const testBoard = [...board];
      testBoard[idx] = "O";
      if (checkWinner(testBoard).winner === "O") return idx;
    }
  }

  return emptyCells[Math.floor(Math.random() * emptyCells.length)];
};

/**
 * AI Computer Move Logic based on difficulty level
 * @param currentBoard - The current game board state
 * @param difficulty - Difficulty level: "easy", "medium", or "hard"
 * @returns The index where the computer should place its move
 */
export const makeComputerMove = (
  currentBoard: Board,
  difficulty: DifficultyLevel = "medium"
): number => {
  // Get all empty cell indices
  const emptyCells = currentBoard
    .map((cell, idx) => (cell === null ? idx : null))
    .filter((idx): idx is number => idx !== null);

  if (emptyCells.length === 0) return -1;

  switch (difficulty) {
    case "easy":
      return getEasyMove(currentBoard, emptyCells);
    case "hard":
      return getHardMove(currentBoard, emptyCells);
    case "medium":
    default:
      return getMediumMove(currentBoard, emptyCells);
  }
};

